import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { I18nService } from 'i18n';
import { FoundingMemberBadgeComponent } from 'shared';
import { CommunityService } from '../../services/community.service';
import { CommunityUnreadService } from '../../services/community-unread.service';
import { CommunityThread, communityHtmlToText } from '../../models/community.models';
import { CommunityBadgesComponent } from '../community-badges/community-badges.component';

@Component({
  selector: 'app-community-dashboard-widget',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslocoModule, FoundingMemberBadgeComponent, CommunityBadgesComponent],
  templateUrl: './community-dashboard-widget.component.html',
  styleUrl: './community-dashboard-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityDashboardWidgetComponent implements OnInit, OnDestroy {
  threads = signal<CommunityThread[]>([]);
  loading = signal(false);

  private langSub?: Subscription;

  constructor(
    private communityService: CommunityService,
    private unread: CommunityUnreadService,
    private i18n: I18nService
  ) {}

  get lang(): string {
    return this.i18n.getLanguage();
  }

  get allThreadsLink(): string {
    return `/${this.lang}/community`;
  }

  ngOnInit(): void {
    this.load();
    // One refresh so the sidebar's unread badge is right before the shell is
    // ever opened (the shell itself polls).
    this.unread.refresh();
    // skip(1) avoids re-loading immediately on subscribe — BehaviorSubject would
    // otherwise emit the current language synchronously and trigger a duplicate
    // request right after the initial load() call above.
    this.langSub = this.i18n.currentLanguage$
      .pipe(skip(1))
      .subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private load(): void {
    this.loading.set(true);
    // No channel: the newest threads across every channel the user may read.
    this.communityService.listThreads({ page: 1, perPage: 2, uiLocale: this.lang }).subscribe({
      next: res => {
        this.threads.set(res.data);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  threadLink(id: number): string[] {
    return [`/${this.lang}/community`, String(id)];
  }

  memberLink(userId: number): string {
    return `/${this.lang}/community/u/${userId}`;
  }

  stripHtml(html: string | undefined): string {
    return communityHtmlToText(html);
  }
}
